import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type PortalProps = {
  children: JSX.Element;
};

/**
 * Portal component that renders children into document.body using createPortal
 * This approach preserves context (like NextRouter) from the parent tree
 */
const Portal = ({ children }: PortalProps) => {
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Create portal container on mount
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const container = document.createElement('div');

      document.body.appendChild(container);
      setPortalContainer(container);
      setMounted(true);

      return () => {
        if (container && document.body.contains(container)) {
          document.body.removeChild(container);
        }
        setPortalContainer(null);
        setMounted(false);
      };
    }
  }, []);

  // Return null during SSR
  if (!mounted || !portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer) as React.ReactPortal;
};

export default Portal;
